// The approval a task waits on, pinned above its chat (D85): the node's question, what the step
// before it reported, and the options as buttons. Answering here is the same signed answer as
// from the apps; an optional note goes to the next step, encrypted on this browser. The card
// leaves as soon as the task moves on (live `task.changed`, else polling).
import { SealCheck } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { useDevice } from "../../device/context";
import { useT } from "../../i18n";
import { cardClass } from "../../ui/Card";
import { cn } from "../../ui/cn";
import { Pill } from "../../ui/status";
import { taskQuery } from "../api";
import { refetchInterval, useLiveTasks } from "../live";
import { ApprovalAnswer } from "./ApprovalAnswer";
import { type PendingApproval, pendingApproval } from "./approval";

export function ApprovalCard({ taskId }: { taskId: string }) {
  const t = useT().tasks.approvalCard;
  const live = useLiveTasks({ tasks: [taskId] });
  const task = useQuery({ ...taskQuery(taskId), refetchInterval: refetchInterval(live) });
  const device = useDevice();
  const pending = task.data ? pendingApproval(task.data) : null;
  if (!task.data || !pending) return null;
  return (
    <section aria-label={t.label} data-testid="approval-card" className={cn(cardClass, "mb-4 space-y-3 border-orange/50 p-4")}>
      <div className="flex flex-wrap items-center gap-2">
        <SealCheck weight="duotone" className="h-5 w-5 text-orange" aria-hidden />
        <h3 className="m-0 text-[14px] font-semibold text-gray-900">{t.title}</h3>
        <Pill tone="orange">{pending.title}</Pill>
      </div>
      <p className="m-0 text-[13.5px] text-gray-900">{pending.question}</p>
      <Previous pending={pending} />
      {device.kind === "linked" ? (
        <ApprovalAnswer
          proto={device.proto}
          device={device}
          taskId={taskId}
          conversationId={task.data.task.conversation_id ?? null}
          pending={pending}
        />
      ) : (
        <p className="m-0 text-[12.5px] text-gray-500">
          {t.linkFirst} <Link to="/devices">{t.linkLink}</Link>
        </p>
      )}
    </section>
  );
}

function Previous({ pending }: { pending: PendingApproval }) {
  const t = useT().tasks.approvalCard;
  const previous = pending.previous;
  if (!previous) return null;
  return (
    <div className="min-w-0 space-y-1.5 rounded-lg bg-gray-50 px-3 py-2 text-[12.5px] text-gray-600 dark:bg-gray-100">
      <div>
        {t.after(previous.title)}
        {previous.outcome ? ` · ${previous.outcome}` : ""}
      </div>
      {previous.outputs.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" aria-label={t.reported}>
          {previous.outputs.map(([key, value]) => (
            <code key={key} className="max-w-full break-all rounded bg-gray-0 px-1.5 py-0.5 font-mono text-[11.5px] text-gray-800 dark:bg-gray-50">
              {key}={value}
            </code>
          ))}
        </div>
      ) : null}
    </div>
  );
}
